import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const emptyEditForm = {
  display_name: '',
  description: '',
  business_unit_code: '',
  contact_email: '',
  contact_phone: '',
  location: '',
};

function countLabel(count, singular, plural = `${singular}s`) {
  const value = Number(count || 0);
  return `${value} ${value === 1 ? singular : plural}`;
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function SetupCard({ icon, title, count, actionLabel, to, onClick, disabled = false }) {
  const buttonProps = to ? { component: RouterLink, to } : { onClick };
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: '100%' }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          {icon}
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {count}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          {...buttonProps}
          variant="outlined"
          startIcon={<AddRoundedIcon />}
          disabled={disabled || (!to && !onClick)}
        >
          {actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
}

export default function BusinessUnitDetailPage(props) {
  const { businessUnitId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', businessUnit: null });
  const [tab, setTab] = useState('overview');
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [addingUsers, setAddingUsers] = useState(false);
  const [resources, setResources] = useState({ capabilities: [], assets: [] });
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editError, setEditError] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBusinessUnit, setDeletingBusinessUnit] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deletingResource, setDeletingResource] = useState('');

  async function loadBusinessUnit() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get(`/partners/user/business-units/${encodeURIComponent(businessUnitId)}`);
      setState({
        loading: false,
        error: '',
        businessUnit: response.data?.business_unit || null,
      });
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || error?.message || 'Unable to load business unit.',
        businessUnit: null,
      });
    }
  }

  async function loadUsers() {
    if (!businessUnitId) {
      return;
    }
    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await axiosInstance.get(`/partners/user/business-units/${encodeURIComponent(businessUnitId)}/users`);
      setUsers(Array.isArray(response.data?.users) ? response.data.users : []);
    } catch (error) {
      setUsers([]);
      setUsersError(error?.response?.data?.message || error?.message || 'Unable to load partner users.');
    } finally {
      setUsersLoading(false);
    }
  }

  async function loadResources() {
    if (!businessUnitId) return;
    setResourcesLoading(true);
    setResourcesError('');
    try {
      const [capabilitiesResponse, assetsResponse] = await Promise.all([
        axiosInstance.get('/partners/user/capabilities', { params: { business_unit_id: businessUnitId } }),
        axiosInstance.get(`/partners/user/business-units/${encodeURIComponent(businessUnitId)}/assets`),
      ]);
      setResources({
        capabilities: capabilitiesResponse.data?.capabilities || capabilitiesResponse.data?.items || [],
        assets: assetsResponse.data?.assets || [],
      });
    } catch (error) {
      setResources({ capabilities: [], assets: [] });
      setResourcesError(error?.response?.data?.message || error?.message || 'Unable to load Business Unit resources.');
    } finally { setResourcesLoading(false); }
  }

  useEffect(() => {
    loadBusinessUnit();
    loadResources();
  }, [businessUnitId]);

  useEffect(() => {
    if (tab === 'users') loadUsers();
  }, [tab, businessUnitId]);

  const businessUnit = state.businessUnit || {};
  const parentPartner = businessUnit.parent_partner || {};
  const canManage = Boolean(businessUnit.can_manage);
  const description = businessUnit.description || businessUnit.profile_description || 'No description provided.';

  const filteredUsers = useMemo(() => {
    const normalizedQuery = normalize(userQuery);
    if (!normalizedQuery) {
      return users;
    }
    return users.filter((user) => normalize([
      user.full_name,
      user.email,
      user.cognito_sub,
    ].filter(Boolean).join(' ')).includes(normalizedQuery));
  }, [userQuery, users]);

  const openUsersDialog = async () => {
    setSelectedUsers([]);
    setUserQuery('');
    setUsersError('');
    setUsersDialogOpen(true);
    await loadUsers();
  };

  const toggleSelectedUser = (userId) => {
    setSelectedUsers((current) => (
      current.includes(userId)
        ? current.filter((value) => value !== userId)
        : [...current, userId]
    ));
  };

  const addSelectedUsers = async () => {
    if (!selectedUsers.length) {
      setUsersError('Select at least one partner user.');
      return;
    }
    setAddingUsers(true);
    setUsersError('');
    try {
      await axiosInstance.post(`/partners/user/business-units/${encodeURIComponent(businessUnitId)}/users`, {
        user_ids: selectedUsers,
      });
      setSelectedUsers([]);
      await loadUsers();
      await loadBusinessUnit();
    } catch (error) {
      setUsersError(error?.response?.data?.message || error?.message || 'Unable to add selected users.');
    } finally {
      setAddingUsers(false);
    }
  };

  const openEditDialog = () => {
    setEditForm({
      display_name: businessUnit.display_name || '',
      description: businessUnit.description || businessUnit.profile_description || '',
      business_unit_code: businessUnit.business_unit_code || '',
      contact_email: businessUnit.contact_email || '',
      contact_phone: businessUnit.contact_phone || '',
      location: businessUnit.location || businessUnit.physical_main_address || '',
    });
    setEditError('');
    setEditDialogOpen(true);
  };

  const saveBusinessUnitDetails = async () => {
    if (!editForm.display_name.trim()) {
      setEditError('Business Unit Name is required.');
      return;
    }
    if (!editForm.business_unit_code.trim()) {
      setEditError('Business Unit Code is required.');
      return;
    }

    setSavingDetails(true);
    setEditError('');
    try {
      const response = await axiosInstance.patch(
        `/partners/user/business-units/${encodeURIComponent(businessUnitId)}`,
        {
          display_name: editForm.display_name,
          description: editForm.description,
          business_unit_code: editForm.business_unit_code,
          contact_email: editForm.contact_email,
          contact_phone: editForm.contact_phone,
          location: editForm.location,
        },
      );
      setState({
        loading: false,
        error: '',
        businessUnit: response.data?.business_unit || businessUnit,
      });
      setEditDialogOpen(false);
    } catch (error) {
      setEditError(error?.response?.data?.message || error?.message || 'Unable to update business unit.');
    } finally {
      setSavingDetails(false);
    }
  };

  const deleteBusinessUnit = async () => {
    setDeletingBusinessUnit(true);
    setDeleteError('');
    try {
      await axiosInstance.delete(`/partners/user/business-units/${encodeURIComponent(businessUnitId)}`);
      navigate('/business-units', { replace: true });
    } catch (error) {
      setDeleteError(error?.response?.data?.message || error?.message || 'Unable to delete business unit.');
    } finally {
      setDeletingBusinessUnit(false);
    }
  };

  const deleteCapability = async (capability) => {
    const capabilityId = capability.capability_definition_id || capability.capability_id;
    if (!window.confirm(`Delete capability "${capability.capability_name || capability.name || 'Capability'}"?`)) {
      return;
    }
    setDeletingResource(`capability:${capabilityId}`);
    setResourcesError('');
    try {
      await axiosInstance.delete(
        `/partners/${encodeURIComponent(capability.partner_id || businessUnit.parent_partner_id)}/capabilities/${encodeURIComponent(capabilityId)}`,
      );
      await Promise.all([loadResources(), loadBusinessUnit()]);
    } catch (error) {
      setResourcesError(error?.response?.data?.message || error?.message || 'Unable to delete capability.');
    } finally {
      setDeletingResource('');
    }
  };

  const deleteAsset = async (asset) => {
    const assetId = asset.asset_id || asset.source_capability_asset_id;
    if (!window.confirm(`Delete asset "${asset.asset_name || asset.name || 'Asset'}"?`)) {
      return;
    }
    setDeletingResource(`asset:${assetId}`);
    setResourcesError('');
    try {
      await axiosInstance.delete(
        `/partners/${encodeURIComponent(asset.partner_id || businessUnit.parent_partner_id)}/assets/${encodeURIComponent(assetId)}`,
      );
      await Promise.all([loadResources(), loadBusinessUnit()]);
    } catch (error) {
      setResourcesError(error?.response?.data?.message || error?.message || 'Unable to delete asset.');
    } finally {
      setDeletingResource('');
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Business Units', href: '/business-units' },
              { label: businessUnit.display_name || 'Business Unit' },
            ]}
          />

          {state.loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Loading business unit...</Typography>
            </Stack>
          ) : state.error ? (
            <Alert severity="error">{state.error}</Alert>
          ) : (
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {parentPartner.display_name || parentPartner.partner_code || 'Partner'}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      {businessUnit.display_name || businessUnit.business_unit_code}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                      {description}
                    </Typography>
                  </Box>
                  <Stack spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-start">
                      <Chip
                        color={businessUnit.your_access ? 'success' : 'default'}
                        variant={businessUnit.your_access ? 'filled' : 'outlined'}
                        label={`Your Access: ${businessUnit.your_access ? 'Yes' : 'No'}`}
                      />
                      <Chip variant="outlined" label={businessUnit.status || 'active'} />
                    </Stack>
                    {canManage ? (
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" startIcon={<EditRoundedIcon />} onClick={openEditDialog}>
                          Edit Details
                        </Button>
                        <Button color="error" variant="outlined" startIcon={<DeleteRoundedIcon />} onClick={() => setDeleteDialogOpen(true)}>
                          Delete
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </Stack>
              </Stack>

              <Tabs value={tab} onChange={(_event, value) => setTab(value)}>
                <Tab value="overview" label="Overview" />
                <Tab value="capabilities" label="Capabilities" />
                <Tab value="assets" label="Assets" />
                <Tab value="users" label="Users" />
              </Tabs>

              {tab === 'overview' ? (
                canManage ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Welcome to {businessUnit.display_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Start setting up this Business Unit.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                        gap: 2,
                      }}
                    >
                      <SetupCard
                        icon={<PrecisionManufacturingRoundedIcon color="primary" />}
                        title="Capabilities"
                        count={countLabel(businessUnit.capability_count, 'configured', 'configured')}
                        actionLabel="View Capabilities"
                        onClick={() => setTab('capabilities')}
                      />
                      <SetupCard
                        icon={<Inventory2RoundedIcon color="primary" />}
                        title="Assets"
                        count={countLabel(businessUnit.asset_count, 'registered', 'registered')}
                        actionLabel="View Assets"
                        onClick={() => setTab('assets')}
                      />
                      <SetupCard
                        icon={<GroupAddRoundedIcon color="primary" />}
                        title="Users"
                        count={countLabel(businessUnit.user_count, 'user')}
                        actionLabel="View Users"
                        onClick={() => { setTab('users'); loadUsers(); }}
                      />
                    </Box>
                  </Stack>
                ) : (
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {businessUnit.display_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {parentPartner.display_name || parentPartner.partner_code}
                      </Typography>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          Description
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {description}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip variant="outlined" label={countLabel(businessUnit.capability_count, 'advertised capability', 'advertised capabilities')} />
                        <Chip variant="outlined" label={countLabel(businessUnit.asset_count, 'registered asset')} />
                      </Stack>
                      {businessUnit.contact_email ? (
                        <Typography variant="body2" color="text.secondary">
                          {businessUnit.contact_email}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                )
              ) : null}

              {tab === 'capabilities' ? (
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {countLabel(businessUnit.capability_count, 'Capability', 'Capabilities')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Capabilities for this Business Unit.
                      </Typography>
                    </Box>
                    {canManage ? (
                      <Button
                        component={RouterLink}
                        to={`/business-units/${encodeURIComponent(businessUnit.business_unit_id)}/capabilities/new`}
                        startIcon={<AddRoundedIcon />}
                        variant="contained"
                      >
                        Add Capability
                      </Button>
                    ) : null}
                  </Stack>
                  {resourcesError ? <Alert severity="error">{resourcesError}</Alert> : null}
                  {resourcesLoading ? <CircularProgress size={24} /> : resources.capabilities.length ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                      {resources.capabilities.map((item) => {
                        const capabilityId = item.capability_definition_id || item.capability_id;
                        const deleting = deletingResource === `capability:${capabilityId}`;
                        return (
                          <Paper key={capabilityId} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.5}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                  {item.capability_name || item.name || 'Capability'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.category_display_name || item.category_name || 'Capability'}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {canManage ? (
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteRoundedIcon />}
                                    disabled={Boolean(deletingResource)}
                                    onClick={() => deleteCapability(item)}
                                  >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                  </Button>
                                ) : null}
                                <Button
                                  size="small"
                                  component={RouterLink}
                                  to={`/capabilities/${encodeURIComponent(capabilityId)}`}
                                >
                                  Open
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : <Typography color="text.secondary">No capabilities are associated with this Business Unit.</Typography>}
                </Paper>
              ) : null}

              {tab === 'assets' ? (
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {countLabel(businessUnit.asset_count, 'Asset')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Registered assets for this Business Unit.
                      </Typography>
                    </Box>
                    {canManage ? (
                      <Button
                        component={RouterLink}
                        to={`/business-units/${encodeURIComponent(businessUnit.business_unit_id)}/assets/new`}
                        startIcon={<AddRoundedIcon />}
                        variant="contained"
                      >
                        Add Asset
                      </Button>
                    ) : null}
                  </Stack>
                  {resourcesError ? <Alert severity="error">{resourcesError}</Alert> : null}
                  {resourcesLoading ? <CircularProgress size={24} /> : resources.assets.length ? (
                    <List>
                      {resources.assets.map((asset) => {
                        const assetId = asset.asset_id || asset.source_capability_asset_id;
                        const deleting = deletingResource === `asset:${assetId}`;
                        return (
                          <ListItem
                            key={assetId}
                            secondaryAction={(
                              <Stack direction="row" spacing={1}>
                                {canManage ? (
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteRoundedIcon />}
                                    disabled={Boolean(deletingResource)}
                                    onClick={() => deleteAsset(asset)}
                                  >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                  </Button>
                                ) : null}
                                <Button size="small" component={RouterLink} to={`/assets/${encodeURIComponent(assetId)}`}>
                                  Open
                                </Button>
                              </Stack>
                            )}
                          >
                            <ListItemText
                              primary={asset.asset_name || asset.name || 'Asset'}
                              secondary={asset.asset_type_display_name || asset.asset_type || ''}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : <Typography color="text.secondary">No assets are registered to this Business Unit.</Typography>}
                </Paper>
              ) : null}

              {tab === 'users' ? (
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {countLabel(businessUnit.user_count, 'User')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Partner users with access to this Business Unit.
                          </Typography>
                        </Box>
                        {canManage ? <Button variant="contained" startIcon={<GroupAddRoundedIcon />} onClick={openUsersDialog}>Add Users</Button> : null}
                      </Stack>
                      {usersLoading ? <CircularProgress size={24} /> : users.filter((user) => user.has_access !== false).length ? <List>{users.filter((user) => user.has_access !== false).map((user) => <ListItem key={user.cognito_sub}><ListItemText primary={user.full_name || user.email || user.cognito_sub} secondary={user.email || ''} /></ListItem>)}</List> : <Typography color="text.secondary">No users are associated with this Business Unit.</Typography>}
                    </Stack>
                  </Paper>
              ) : null}
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog open={editDialogOpen} onClose={() => !savingDetails && setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Business Unit Details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {editError ? <Alert severity="error">{editError}</Alert> : null}
            <TextField
              label="Business Unit Name"
              value={editForm.display_name}
              onChange={(event) => setEditForm((current) => ({ ...current, display_name: event.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={editForm.description}
              onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))}
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Business Unit Code"
              value={editForm.business_unit_code}
              onChange={(event) => setEditForm((current) => ({ ...current, business_unit_code: event.target.value.toUpperCase() }))}
              required
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Contact Email"
                type="email"
                value={editForm.contact_email}
                onChange={(event) => setEditForm((current) => ({ ...current, contact_email: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Contact Phone"
                value={editForm.contact_phone}
                onChange={(event) => setEditForm((current) => ({ ...current, contact_phone: event.target.value }))}
                fullWidth
              />
            </Stack>
            <TextField
              label="Location"
              value={editForm.location}
              onChange={(event) => setEditForm((current) => ({ ...current, location: event.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={savingDetails}>Cancel</Button>
          <Button variant="contained" onClick={saveBusinessUnitDetails} disabled={savingDetails}>
            {savingDetails ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deletingBusinessUnit && setDeleteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete Business Unit?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
            <Typography>
              Delete <strong>{businessUnit.display_name || businessUnit.business_unit_code}</strong>? This cannot be undone.
            </Typography>
            <Alert severity="warning">
              Assets, capabilities, active requests, and other dependent records must be removed before this Business Unit can be deleted.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletingBusinessUnit}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteBusinessUnit} disabled={deletingBusinessUnit}>
            {deletingBusinessUnit ? 'Deleting...' : 'Delete Business Unit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={usersDialogOpen} onClose={() => !addingUsers && setUsersDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Partner Users</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {usersError ? <Alert severity="error">{usersError}</Alert> : null}
            <TextField
              value={userQuery}
              onChange={(event) => setUserQuery(event.target.value)}
              placeholder={`Search ${parentPartner.display_name || 'Partner'} users...`}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {usersLoading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Loading users...</Typography>
              </Stack>
            ) : (
              <List dense disablePadding>
                {filteredUsers.map((user) => {
                  const checked = user.has_access || selectedUsers.includes(user.cognito_sub);
                  return (
                    <ListItem key={user.cognito_sub} disablePadding>
                      <ListItemButton
                        disabled={user.has_access}
                        onClick={() => toggleSelectedUser(user.cognito_sub)}
                      >
                        <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                        <ListItemText
                          primary={user.full_name || user.email || user.cognito_sub}
                          secondary={user.has_access ? 'Already has access' : user.email}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {!filteredUsers.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No partner users match the current search.
                  </Typography>
                ) : null}
              </List>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialogOpen(false)} disabled={addingUsers}>Cancel</Button>
          <Button variant="contained" onClick={addSelectedUsers} disabled={addingUsers || !selectedUsers.length}>
            {addingUsers ? 'Adding...' : 'Add Selected Users'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
